import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { Interval } from "luxon";
import React from "react";
import { useUnmount } from "usehooks-ts";
import {
  useCurrentProposal,
  useUpdateCurrentProposal,
} from "../../chat-plus-calendar/CurrentProposalContext";
import {
  TradeoffBlock,
  usePersistedProposal,
} from "../../chat-plus-calendar/PersistedProposalContext";
import {
  useReadProposalOptionsOverlayV2,
  useUpdateProposalOptionsOverlayV2,
} from "../../chat-plus-calendar/util/ProposalOptionsOverlayContextV2";
import { ProposalOptionCard } from "../proposal-option-card-v2/ProposalOptionCard";
import { ProposalOptionRailCard } from "../proposal-option-card-v2/ProposalOptionRailCard";

export type ProposalOptionSpacing = "full" | "small" | "none";

export const ProposalOption = ({
  interval,
  tradeoffBlocks,
  label,
  popoverSide,
  spacing,
  rail,
}: {
  interval: Interval;
  tradeoffBlocks: TradeoffBlock[];
  label: string;
  popoverSide: "left" | "right" | "bottom";
  spacing?: ProposalOptionSpacing;
  rail: boolean;
}) => {
  const track = useTracking();
  const { setHoveredSuggestion } = useUpdateProposalOptionsOverlayV2();
  const { hoveredSuggestion } = useReadProposalOptionsOverlayV2();
  const { suggestedTimeOptions } = usePersistedProposal();
  const { updateTime } = useUpdateCurrentProposal();
  const { currentProposal } = useCurrentProposal();

  const selectedSuggestedTimeOptionIndex =
    !suggestedTimeOptions || !currentProposal?.startTime || !currentProposal?.endTime
      ? undefined
      : suggestedTimeOptions.findIndex((option) => {
          const currentInterval = Interval.fromDateTimes(
            currentProposal.startTime,
            currentProposal.endTime,
          );
          return (
            option.interval.start.toMillis() === currentInterval.start.toMillis() &&
            option.interval.end.toMillis() === currentInterval.end.toMillis()
          );
        });

  const selectedSuggestion =
    suggestedTimeOptions?.[selectedSuggestedTimeOptionIndex ?? 0]?.interval ?? null;

  const isSelected = selectedSuggestion?.equals(interval) ?? false;
  const isHovered = hoveredSuggestion === interval.toISO();

  const onMouseEnter = () => {
    setHoveredSuggestion(interval.toISO());
  };

  const onMouseLeave = () => {
    setHoveredSuggestion(null);
  };

  useUnmount(() => {
    // Remove the time suggestion overlay when the component unmounts.
    setHoveredSuggestion(null);
  });

  const onClickOption = () => {
    updateTime(interval.start, interval.end);
    setHoveredSuggestion(null);
    track(TrackingEvents.PROPOSAL_OPTIONS_OVERLAY.OPTION_CLICKED);
  };

  return (
    <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} className="cw-h-full cw-w-full">
      {rail ? (
        <ProposalOptionRailCard
          emphasized={isHovered}
          label={label}
          onClick={onClickOption}
          popoverSide={popoverSide}
          selected={isSelected}
          tradeoffs={tradeoffBlocks}
          spacing={spacing}
        />
      ) : (
        <ProposalOptionCard
          emphasized={isHovered}
          onClick={onClickOption}
          popoverSide={popoverSide}
          selected={isSelected}
          text={label}
          tradeoffs={tradeoffBlocks}
          spacing={spacing}
        />
      )}
    </div>
  );
};
